import {customElement, property, query} from "lit/decorators.js";
import {html, type TemplateResult} from "lit";
import {unsafeHTML} from "lit/directives/unsafe-html.js";
import type {EopYoutubePlayer} from "../../module/socialMedia/youtube/youtubePlayer";
import {Nav} from "../../common/nav";
import {resolve} from "../../container";
import {EventBus} from "../../common/eventBus";
import type {Nullable} from "../../bootstrap/common/types";
import {trackingTeaserInteract} from "../../tracking/trackingAttributes";
import {unsafeSVG} from "lit/directives/unsafe-svg.js";
import {PLAY_ICON} from "../../common/svgIcons";
import {UnLitElement} from "../../common/elements";

export const YOUTUBE_SHORT_TILE_PLAY_VIDEO_EVENT = "youtube-short-tile-play-video";

@customElement("eop-youtube-short-tile")
export class EopYoutubeShortTile extends UnLitElement {

    @property({attribute: "headline", type: String})
    public headline: string;
    @property({attribute: "sub-headline", type: String})
    public subHeadline: string;
    @property({attribute: "video-id", type: String})
    public videoId: string;
    @property({attribute: "alt", type: String})
    public alt: string;
    @property({attribute: "playing", reflect: true, type: Boolean})
    public playing: boolean = false;
    @query("eop-youtube-player")
    private youtubePlayer?: EopYoutubePlayer;

    public constructor(
        private nav: Nav = resolve(Nav),
        private eventBus: EventBus = resolve(EventBus)
    ) {
        super();
    }

    public render(): TemplateResult {
        return html`
            <div
                    @click=${this.startPlayingVideo}
                    @keydown=${this.startPlayingVideo}
                    ${trackingTeaserInteract(this.headline ?? "youtube-short-tile")}
                    class="youtube-short-tile"
                    tabindex="0"
            >
                ${this.renderImage()}
                <div class="shadow-overlay"></div>
                <div class="play-icon-container">
                    <div class="play-button">
                        <div class="play">${unsafeSVG(PLAY_ICON)}</div>
                    </div>
                </div>
                ${this.renderContent()}
                ${this.renderYoutubePlayer()}
            </div>
        `;
    }

    private renderYoutubePlayer(): Nullable<TemplateResult> {
        return html`
            <eop-load-after-confirmation
                    module-name="youtube"
                    confirmation-text-key="YOUTUBE_CONFIRMATION_TEXT"
                    button-label-key="YOUTUBE_CONFIRMATION_BUTTON_LABEL"
                    class="youtube-container"
            >
                <span eop-slot="content-to-confirm">
                    <eop-youtube-player
                            video-id=${this.videoId}
                            aspect-ratio="vertical"
                            origin-stage=${this.nav.origin()}
                            class="youtube-player"
                    >
                        <div class="video-placeholder"></div>
                    </eop-youtube-player>
                </span>
            </eop-load-after-confirmation>
        `;
    }

    public pauseVideo(): void {
        if (this.playing) {
            this.youtubePlayer!.pauseVideo();
            this.playing = false;
        }
    }

    private startPlayingVideo(e: Event): void {
        if (e?.type === "keydown" && (e as KeyboardEvent).key !== "Enter") {
            return;
        }
        this.playVideo();
    }

    private playVideo(): void {
        if (!this.playing) {
            this.youtubePlayer!.playVideo();
            this.eventBus.dispatchEvent(YOUTUBE_SHORT_TILE_PLAY_VIDEO_EVENT);
            this.playing = true;
        }
    }

    private renderImage(): TemplateResult {
        const youtubeThumbnailUrl = `https://i.ytimg.com/vi/${this.videoId}/maxresdefault.jpg`;
        return html`
            <div class="tile-image">
                <eop-plain-image src=${youtubeThumbnailUrl} alt=${this.alt} class="eop-image"></eop-plain-image>
            </div>
        `;
    }

    public static extractYoutubeVideoIdFromUrl(videoUrl: string): string {
        return videoUrl.split("embed/")[1];
    }

    private renderContent(): TemplateResult {
        return html`
            <div class="tile-text">
                <div class="tile-headlines">
                    ${this.renderHeadline()}
                    ${this.renderSubHeadline()}
                </div>
            </div>
        `;
    }

    private renderHeadline(): Nullable<TemplateResult> {
        return this.headline ? html`
            <div class="tile-headline">${unsafeHTML(this.headline)}</div>` : null;
    }

    private renderSubHeadline(): Nullable<TemplateResult> {
        return this.subHeadline ? html`
            <div class="tile-sub-headline">${unsafeHTML(this.subHeadline)}</div>` : null;
    }
}